const MODULE_NAME = "_13_current_week_largest_open_pos_chart";

import React, { useEffect, useState } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import axios from "axios";
import { cachedGet } from "./apiCache.js";
import LargestOpenPosTable from "./_13_current_week_largest_open_pos_table.js";
import { useDateRange } from "./dateRangeContext.js";
import { useTagFilter } from "./TagFilterContext.js";
import { FullscreenIcon, getFullscreenContainerStyles, getFullscreenHeaderStyles, getFullscreenLayoutStyles, useFullscreenAttribute } from "./fullscreenComponentUtils.js";
import { useModuleVisibility, getActiveModule } from "./useModuleVisibility.js";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

const WINDOW_OPTIONS = [
    { label: "Last 15 days", value: 15 },
    { label: "Last 30 days", value: 30 },
    { label: "Last 90 days", value: 90 },
    { label: "YTD", value: "YTD" }
];

const PRICE_BUCKET_LABELS = [
    "under $10",
    "10-30",
    "31-99",
    "100-200",
    "201-300",
    "300+"
];

function getRiskFilterRange(filter) {
    switch (filter) {
        case "1X": return { min: 0, max: 100 };
        case "1-3X": return { min: 101, max: 399 };
        case "4X": return { min: 400, max: Infinity };
        default: return { min: 0, max: Infinity };
    }
}

function Tooltip({ text }) {
    return (
        <span
            style={{
                marginLeft: 4,
                verticalAlign: "middle",
                cursor: "pointer",
                color: "#fff",
                fontSize: 18
            }}
            title={text}
        >
            🛈
        </span>
    );
}

function getDeviceIsDesktop() {
    return (window.innerWidth >= 1024 && window.innerHeight >= 768) ? 1 : 0;
}

function getMostPopularPriceBucket(data) {
    const bucketCounts = {};
    data.forEach(row => {
        const bucket = row["Price range"];
        if (bucket && bucket !== "N/A") {
            bucketCounts[bucket] = (bucketCounts[bucket] || 0) + 1;
        }
    });
    let maxCount = 0;
    let mostPopular = null;
    Object.entries(bucketCounts).forEach(([bucket, count]) => {
        if (count > maxCount) {
            maxCount = count;
            mostPopular = bucket;
        }
    });
    return mostPopular;
}

export default function LargestOpenPosChart(props) {
    const {
        activeDate: propActiveDate = null,
        apiUrl = `${process.env.REACT_APP_API_URL}/api/largest_open_pos`,
        screen,
        refreshKey
    } = props;

    const { windowDays } = useDateRange();
    const { globalTagFilter } = useTagFilter();

    // Check if in fullscreen mode
    const isVisible = useModuleVisibility(MODULE_NAME);
    const isFullscreen = getActiveModule() === MODULE_NAME;

    // Use centralized hook for fullscreen attribute
    useFullscreenAttribute(isFullscreen);

    // Set fullscreen indicator on mount/unmount
    useEffect(() => {
        if (isFullscreen) {
            document.documentElement.setAttribute('data-fullscreen', 'true');
            return () => document.documentElement.removeAttribute('data-fullscreen');
        }
    }, [isFullscreen]);

    const [activeDate, setActiveDate] = useState(propActiveDate);
    const [data, setData] = useState([]);
    const [dateCol, setDateCol] = useState("Date");
    const [error, setError] = useState("");
    const [riskFilter, setRiskFilter] = useState("");
    const [plMethod, setPlMethod] = useState(() => sessionStorage.getItem("plMethod") || "fifo");
    const [debugInfo, setDebugInfo] = useState({});
    const [showDebug, setShowDebug] = useState(false);
    const [intradayRiskTooltip, setIntradayRiskTooltip] = useState("");
    const [collapsed, setCollapsed] = useState(true);

    useEffect(() => {
        if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
            const handler = () => {
                const newMethod = sessionStorage.getItem("plMethod") || "fifo";
                setPlMethod(newMethod);
                setDebugInfo(info => ({ ...info, plMethodChanged: newMethod }));
                console.debug("[plMethodChanged]", newMethod);
            };
            window.addEventListener("plMethodChanged", handler);
            return () => window.removeEventListener("plMethodChanged", handler);
        }
    }, []);

    // API data fetch effect
    useEffect(() => {
        // Remove user_id from params, backend uses session
        const params = {
            window_days: windowDays,
            active_date: activeDate,
            pl_method: plMethod,
            refreshKey
        };
        
        // Add tag filter if provided
        if (globalTagFilter) {
            params.tag_filter = globalTagFilter;
        }

        setDebugInfo(info => ({ ...info, apiParams: params }));
        console.debug("[API Request Params]", params);

        cachedGet(apiUrl, params, { withCredentials: true })
            .then((res) => {
                const apiData = Array.isArray(res.data.data) ? res.data.data : [];
                setData(apiData);
                setDateCol(res.data.date_col);
                setError("");
                setIntradayRiskTooltip(res.data.intraday_risk_chart);
                setDebugInfo(info => ({
                    ...info,
                    apiResponse: res.data,
                    dataSample: apiData.slice(0, 3) || [],
                }));
                console.debug("[API Response]", res.data);
            })
            .catch((err) => {
                setError(
                    err.response?.data?.error ||
                    "Failed to load largest open position data."
                );
                setDebugInfo(info => ({
                    ...info,
                    apiError: err.response?.data || err.message
                }));
                console.error("[API Error]", err.response?.data || err.message);
            });
    }, [windowDays, activeDate, apiUrl, plMethod, refreshKey, globalTagFilter]);

    function parseRisk(val) {
        if (typeof val === "string") {
            return parseFloat(val.replace("%", ""));
        }
        return val;
    }

    let filteredData = Array.isArray(data) ? data : [];
    if (windowDays === "YTD") {
        filteredData = groupByWeek(filteredData, dateCol);
    }
    if (riskFilter) {
        const { min, max } = getRiskFilterRange(riskFilter);
        filteredData = filteredData.filter(row => {
            const risk = parseRisk(row["Risk %"]);
            return risk >= min && risk <= max;
        });
    }

    useEffect(() => {
        setDebugInfo(info => ({
            ...info,
            filteredDataSample: filteredData.slice(0, 3),
            totalPL: filteredData.reduce((sum, row) => {
                let val = row["Symbol profit"];
                if (typeof val !== "number") val = parseFloat(val);
                return sum + (isNaN(val) ? 0 : val);
            }, 0)
        }));
        console.debug("[Filtered Data Sample]", filteredData.slice(0, 3));
    }, [filteredData]);

    const chartLabels = filteredData.map(row => formatAPIDate(row[dateCol]));
    const riskPercent = filteredData.map(row => {
        const percent = typeof row["Risk %"] === "string" ? parseFloat(row["Risk %"].replace("%", "")) : row["Risk %"];
        return percent;
    });
    const symbolProfit = filteredData.map(row => {
        let val = row["Symbol profit"];
        if (typeof val !== "number") val = parseFloat(val);
        return isNaN(val) ? 0 : val;
    });

    const combinedChartData = {
        labels: chartLabels,
        datasets: [
            {
                type: "line",
                label: "Risk %",
                data: riskPercent,
                fill: true,
                backgroundColor: "rgba(255, 111, 97, 0.2)",
                borderColor: "#FF6F61",
                pointBackgroundColor: "#FF6F61",
                tension: 0.3,
                yAxisID: "y",
            },
            {
                type: "line",
                label: "Daily Realized Profit",
                data: symbolProfit,
                borderColor: "#00FFFF",
                backgroundColor: "rgba(0,255,255,0.15)",
                borderWidth: 3,
                pointRadius: 4,
                pointBackgroundColor: "#00FFFF",
                fill: true,
                yAxisID: "y1",
                tension: 0.2,
            }
        ],
    };

    const combinedChartOptions = {
        responsive: false,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true },
            title: { display: true, text: "Largest Open Position Risk (%) & Profit" },
        },
        scales: {
            y: {
                type: "linear",
                position: "left",
                title: { display: true, text: "Risk (%)" },
                beginAtZero: true,
                min: 0,
                max: Math.max(...riskPercent, 100),
                grid: { drawOnChartArea: true },
                offset: false,
            },
            y1: {
                type: "linear",
                position: "right",
                title: { display: true, text: "Profit ($)" },
                beginAtZero: false,
                min: Math.min(0, ...symbolProfit),
                grid: { drawOnChartArea: false },
                offset: false,
            },
        },
    };

    const isMobile = useIsMobile();
    const chartWidth = isMobile ? 340 : 900;
    const chartHeight = isMobile ? 220 : 480;

    const tableData = filteredData;
    const tableDateCol = dateCol;
    const tableTooltip = intradayRiskTooltip;
    const [showTable, setShowTable] = useState(true);

    // Return null if component is not visible
    if (!isVisible) return null;

    return (
        <div
            className={`collapse-toggle-container${collapsed ? " collapsed" : ""}`}
            style={getFullscreenContainerStyles(isFullscreen)}
            data-analytics={MODULE_NAME}
        >
            <div style={getFullscreenHeaderStyles(isFullscreen)}>
                <h4
                    className="collapse-toggle-headline"
                    onClick={() => setCollapsed(!collapsed)}
                    style={{ cursor: "pointer", userSelect: "none", marginTop: 0, marginBottom: collapsed ? 0 : 16, flex: 1 }}
                >
                    📊 Largest intra day position –  {windowDays === 90 ? "Week" : "Day"} (last {windowDays} days)
                    <Tooltip text={intradayRiskTooltip} />
                </h4>
                {!isFullscreen && (
                    <FullscreenIcon 
                        moduleName={MODULE_NAME}
                        title="Largest Open Position Chart"
                        additionalParams={{ windowDays }}
                    />
                )}
            </div>
            {!collapsed && (
                <>
                    {/* Chart only, no local window selection buttons */}
                    <div style={{...getFullscreenLayoutStyles(isFullscreen), marginTop: 16, marginBottom: isMobile ? 32 : 56, height: chartHeight, width: "100%" }}>
                        <Chart
                            type="bar"
                            data={combinedChartData}
                            options={combinedChartOptions}
                            height={chartHeight}
                            width={chartWidth}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);
    useEffect(() => {
        function handleResize() {
            setIsMobile(window.innerWidth <= 700);
        }
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    return isMobile;
}

function groupByWeek(data, dateCol) {
    const weeks = {};
    data.forEach(row => {
        const date = new Date(row[dateCol]);
        if (isNaN(date)) return;
        const year = date.getFullYear();
        const week = getISOWeek(date);
        const key = `${year}-W${week}`;
        if (!weeks[key]) {
            weeks[key] = { ...row, count: 1 };
            weeks[key][dateCol] = key;
        } else {
            Object.keys(row).forEach(col => {
                if (typeof row[col] === "number" && !isNaN(row[col])) {
                    weeks[key][col] = (weeks[key][col] || 0) + row[col];
                }
            });
            weeks[key].count += 1;
        }
    });
    return Object.values(weeks).map(row => {
        if (row["Risk %"] !== undefined && row.count) {
            row["Risk %"] = row["Risk %"] / row.count;
        }
        return row;
    });
}

function getISOWeek(date) {
    const tmp = new Date(date.getTime());
    tmp.setHours(0, 0, 0, 0);
    tmp.setDate(tmp.getDate() + 3 - ((tmp.getDay() + 6) % 7));
    const week1 = new Date(tmp.getFullYear(), 0, 4);
    return 1 + Math.round(((tmp - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

const buttonStyle = (active) => ({
    background: active ? "#00b894" : "#232946",
    color: "#fff",
    border: "1px solid #444",
    borderRadius: 4,
    padding: "6px 16px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    marginRight: 8,
    marginBottom: 6,
    minWidth: 60,
});

function formatAPIDate(dateStr) {
    if (!dateStr || typeof dateStr !== "string") return dateStr;
    let dateOnly = dateStr.split("T")[0].split(" ")[0];
    const isoMatch = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return `${month}/${day}/${year.slice(-2)}`;
    }
    const usMatch = dateOnly.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
    if (usMatch) return dateOnly;
    const d = new Date(dateStr);
    if (!isNaN(d)) {
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const yy = String(d.getFullYear()).slice(-2);
        return `${mm}/${dd}/${yy}`;
    }
    return dateOnly;
}

